import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdyenCheckout, Dropin } from '@adyen/adyen-web/auto';
import '@adyen/adyen-web/styles/adyen.css';
import * as translator from '../../../utils/translator';
import { clientConfig } from '../../../config';
import { makePayment, submitPaymentDetails, handlePaymentResult, handlePaymentError, validateCustomerSession, MakePaymentParams } from '../paymentService';
import type { AdyenInitData, PaymentContext, PaymentCallbacks, PaymentResult, UserClickTracking } from '../paymentTypes';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import sepaInstantIcon from '../../../icons/sepa-instant.svg';

// Lets callers (e.g. the customer-session-less repay flow) redirect the server calls this
// form normally makes to payment/make_payment and payment/payment_details, without forking
// the component. Regular checkout omits this prop and keeps its existing behavior untouched.
export interface PaymentServiceOverrides {
  makePayment: (params: MakePaymentParams) => Promise<PaymentResult>;
  submitPaymentDetails?: (data: any) => Promise<PaymentResult>;
  skipSessionValidation?: boolean;
  onSuccess?: (result: PaymentResult) => void;
}

interface AdyenCheckoutFormProps {
  adyenInitData: AdyenInitData;
  paymentContext: PaymentContext;
  callbacks: PaymentCallbacks;
  tracking: UserClickTracking;
  serviceOverrides?: PaymentServiceOverrides;
}

// ============================================================================
// Adyen Checkout Form Component
// ============================================================================

const AdyenCheckoutForm: React.FC<AdyenCheckoutFormProps> = ({adyenInitData, paymentContext, callbacks, tracking, serviceOverrides}) => {
    const navigate = useNavigate();
    const dropinRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const disclaimerRef = useRef<HTMLDivElement | null>(null);

    // Keep a ref to the latest callbacks so Adyen closure handlers always read current values
    const callbacksRef = useRef(callbacks);
    useEffect(() => { callbacksRef.current = callbacks; }, [callbacks]);
  
    const { country, language, hashID, sepaPositionVariant } = paymentContext;
    const relocationString = `/${country}/account/my-orders/${hashID}/details`;
  
    // ============================================================================
    // Helpers
    // ============================================================================
    // const setDropinStatus = useCallback((status: string) => {dropinRef.current?.setStatus(status);}, []);
    const navigateTo = useCallback((path: string) => navigate(path), [navigate]);
  
    // defining the onPaymentAction callback
    callbacks.onPaymentAction = async (action: any) => {
      await dropinRef.current?.handleAction(action);
    };

    // ============================================================================
    // Call Payment in Server
    // ============================================================================
  
    const callPaymentInServer = useCallback(
      async (data: any, dropin: any, actions?: { resolve: (data: any) => void; reject: () => void }) => {
        if (!serviceOverrides?.skipSessionValidation && !validateCustomerSession()) {
          actions?.reject();
          navigate({ pathname: relocationString, search: 'showRedirectModal=true' });
          return;
        }

        const activePaymentMethod = dropinRef.current?.activePaymentMethod;
        const paymentMethodType = activePaymentMethod?.type || data?.paymentMethod?.type;
        const requiresVerification = callbacksRef.current.paymentTypesRequiringIdVerification?.includes(paymentMethodType);
        
        if (requiresVerification && !callbacksRef.current.isIdVerified) {
          callbacksRef.current.onIdVerificationRequired?.(paymentMethodType);
          actions?.reject();
          dropin.setStatus('ready');
          return;
        }
  
        try {
          const result = await (serviceOverrides?.makePayment ?? makePayment)({
            gatewayState: data,
            context: paymentContext,
            provider: 'adyen',
            tracking,
          });

          if (result.providerResponse?.action) {
            await dropin.handleAction(result.providerResponse.action);
            return;
          }

          handlePaymentResult({
            result,
            context: paymentContext,
            callbacks: callbacksRef.current,
            navigate: navigateTo,
            setDropinStatus: (status) => dropin.setStatus(status),
            onSuccessOverride: serviceOverrides?.onSuccess,
          });
          actions?.resolve({ resultCode: result.resultCode });
        } catch (error) {
          handlePaymentError({
            error,
            context: paymentContext,
            callbacks: callbacksRef.current,
            setDropinStatus: (status) => dropin.setStatus(status),
          });
          actions?.reject();
        }
      },
      [paymentContext, navigate, navigateTo, relocationString, tracking, serviceOverrides]
    );

    // ============================================================================
    // Trigger Payment Submit (for ID verification modal)
    // ============================================================================

    const triggerPaymentSubmit = useCallback(() => {
      if (!dropinRef.current) {
        return;
      }

      // Try to find and click the active payment method's submit button
      const activePaymentMethod = dropinRef.current.activePaymentMethod;
      const paymentType = activePaymentMethod?.type;
      const submitButton = document.querySelector(`[data-payment-method-type="${paymentType}"] button[type="submit"], .adyen-checkout__${paymentType} button, [class*="${paymentType}"] button`);
      
      if (submitButton) {
        (submitButton as HTMLElement).click();
      } else {
        // Fallback: try to get the active payment method and submit
        if (activePaymentMethod) {
          // Try to submit using the dropin's submit method if available
          try {
            const state = activePaymentMethod.data;
            if (state) {
              callPaymentInServer(state, dropinRef.current);
            }
          } catch (error) {
            window.clientLogger.error('Error triggering payment submit', error);
          }
        }
      }
    }, [callPaymentInServer]);

    // Expose triggerPaymentSubmit via callback
    useEffect(() => {
      if (callbacks.onTriggerPaymentSubmit !== undefined) {
        // Store triggerPaymentSubmit in the callback so parent can call it
        (callbacks as any).onTriggerPaymentSubmit = triggerPaymentSubmit;
      }
    }, [triggerPaymentSubmit, callbacks]);
  
    // ============================================================================
    // Call Details in Server (3DS)
    // ============================================================================

    const callDetailsInServer = useCallback(async (data: any, dropin: any, actions?: { resolve: (data: any) => void; reject: () => void }) => {
      try {
        const result = await (serviceOverrides?.submitPaymentDetails ?? submitPaymentDetails)(data);

        if (result.providerResponse?.action) {
          actions?.resolve({
            resultCode: result.resultCode,
            action: result.providerResponse.action,
          });
          return;
        }

        handlePaymentResult({
          result,
          context: paymentContext,
          callbacks: callbacksRef.current,
          navigate: navigateTo,
          setDropinStatus: (status) => dropin.setStatus(status),
          onSuccessOverride: serviceOverrides?.onSuccess,
        });
        actions?.resolve({ resultCode: result.resultCode });
      } catch (error) {
        window.clientLogger.error('error in callDetailsInServer', error);
        dropin.setStatus('ready');
        callbacksRef.current.onError(error);
        actions?.reject();
      }
    }, [paymentContext, navigateTo, serviceOverrides]);
  
    // ============================================================================
    // Build Adyen Configuration
    // ============================================================================
  
    const buildAdyenConfig = useCallback(() => {
      const {
        gatewayResult,
        reorderedGatewayResult,
        clientKey,
        environment,
        isSubscription,
        orderAddresses,
        customerData,
        reorderedPaymentMethods,
      } = adyenInitData;
      const { orderPrice, currency, currencyRate } = paymentContext;


      return {
        paymentMethodsResponse: reorderedPaymentMethods ? (reorderedGatewayResult || gatewayResult) : gatewayResult,
        clientKey,
        locale: `${language.toLowerCase()}-${orderAddresses.country_id}`,
        environment,
        countryCode: country.toUpperCase(),
        onError: (error: any) => {
          window.clientLogger.error('adyen error', error);
          callbacksRef.current.onError(error);
        },
        analytics: { enabled: true },
        onSubmit: (state: any, component: any, actions: any) => {
          const paymentMethod = state.data?.paymentMethod;
          const requiresVerification = callbacksRef.current.paymentTypesRequiringIdVerification?.includes(paymentMethod?.type);
          if (requiresVerification && !callbacksRef.current.isIdVerified) {
            callbacksRef.current.onIdVerificationRequired?.(paymentMethod?.type);
            actions.reject();
            return;
          }
          callPaymentInServer(state.data, component, actions);
        },
        onAdditionalDetails: (state: any, component: any, actions: any) => {
          callDetailsInServer(state.data, component, actions);
        },
        onPaymentCompleted: () => {},
        onPaymentFailed: () => {},
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            enableStoreDetails: !isSubscription,
            billingAddressRequired: true,
            hideCVC: false,
            data: {
              holderName: `${customerData.first_name} ${customerData.last_name}`,
              billingAddress: {
                country: orderAddresses.country_id,
                city: orderAddresses.city,
                street: orderAddresses.street,
                postalCode: orderAddresses.zip,
                houseNumberOrName: orderAddresses.house_number,
              },
            },
            name: translator.translate(language.toLowerCase(), 'payment', 'placeholders/paymentMethod'),
            placeholders: {
              cardNumber: translator.translate(language.toLowerCase(), 'payment', 'placeholders/cardNumber'),
              expiryDate: translator.translate(language.toLowerCase(), 'payment', 'placeholders/expiryDate'),
              securityCodeThreeDigits: translator.translate(language.toLowerCase(), 'payment', 'placeholders/securityCodeThreeDigits'
              ),
            },
            onSubmit: (state: any, component: any, actions: any) => {
              callPaymentInServer(state.data, component, actions);
            },
          },
          directEbanking: {},
          applepay: {
            amount: { currency, value: orderPrice * 100 * currencyRate},
            onCancel: (_: any, dropIn: any) => dropIn.setStatus('ready'),
            onError: (_: any, dropIn: any) => dropIn.setStatus('ready'),
            countryCode: country.toUpperCase(),
          },
          googlepay: {
            amount: { currency, value: orderPrice * 100 * currencyRate},
            onCancel: (_: any, dropIn: any) => dropIn.setStatus('ready'),
            onError: (_: any, dropIn: any) => dropIn.setStatus('ready'),
            countryCode: country.toUpperCase(),
            environment: clientConfig.ENV === 'production' ? 'PRODUCTION' : 'TEST',
            merchantId: clientConfig.GOOGLE_PAY_MERCHANT_ID,
          },
          paybybank: {
            name: 'SEPA Echtzeitüberweisung',
            icon: sepaInstantIcon,
            countryCode: country.toUpperCase(),
            onCancel: (_: any, dropIn: any) => dropIn.setStatus('ready'),
            onError: (_: any, dropIn: any) => dropIn.setStatus('ready'),
          },
          paypal: {
            amount: { currency, value: orderPrice * currencyRate},
            blockPayPalPayLaterButton: false,
            onCancel: (_: any, dropIn: any) => dropIn.setStatus('ready'),
            onError: (_: any, dropIn: any) => dropIn.setStatus('ready'),
          },
        },
      };
    }, [adyenInitData, paymentContext, country, language, callPaymentInServer, callDetailsInServer]);
  
    // ============================================================================
    // Initialize Adyen
    // ============================================================================
  
    useEffect(() => {
      const initializeAdyen = async () => {
        const config = buildAdyenConfig();
        const { paymentMethodsConfiguration, ...checkoutConfig } = config;
  
        const checkout = await AdyenCheckout(checkoutConfig);
  
        dropinRef.current = new Dropin(checkout, {
          paymentMethodsConfiguration,
          openFirstPaymentMethod: true,
          showStoredPaymentMethods: true,
          showRadioButton: true,
          // Focus on the selected payment method when user chooses it
          onSelect: (paymentMethod: any) => {
            MainTracker.track('custom', 'Payment Method Selected', {
              selected_payment_method: paymentMethod?.type,
              ...(sepaPositionVariant && { sepa_position_test: sepaPositionVariant }),
            });

            // Check if payment method requires ID verification and is not yet verified
            const requiresVerification = callbacksRef.current.paymentTypesRequiringIdVerification?.includes(paymentMethod?.type);
            if (requiresVerification && !callbacksRef.current.isIdVerified) {
              callbacksRef.current.onIdVerificationRequired?.(paymentMethod?.type);
            }

            // Find the selected payment method element and focus on it
            setTimeout(() => {
              // Try to find the selected payment method element
              const selectedElement = document.querySelector('.adyen-checkout__payment-method--selected');
              
              if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                (selectedElement as HTMLElement).focus();
              }
            }, 100);
          },

          onReady: () => {
            // PayPal verification disclaimer injection
            if (callbacksRef.current.paymentTypesRequiringIdVerification?.includes('paypal') && !callbacksRef.current.isIdVerified && !callbacksRef.current.hidePaypalDisclaimer) {
              const paymentMethods = containerRef.current?.querySelectorAll('.adyen-checkout__payment-method') ?? [];
              const paypalMethod = Array.from(paymentMethods).find((el) => {
                const img = el.querySelector('img');
                const text = el.textContent?.toLowerCase() ?? '';
                return img?.src?.toLowerCase().includes('paypal') || text.includes('paypal');
              });

              if (paypalMethod) {
                const disclaimer = document.createElement('div');
                disclaimer.className = 'paypal-verification-disclaimer';
                disclaimer.textContent = translator.translate(language.toLowerCase(), 'payment', callbacksRef.current.isExpressOrder ? 'idVerificationDisclaimerExpress' : 'idVerificationDisclaimer');
                paypalMethod.insertAdjacentElement('afterend', disclaimer);
                disclaimerRef.current = disclaimer;
              }
            }

            // Radio button DOM manipulation
            const headers = document.querySelectorAll('.adyen-checkout__payment-method__header');
            const firstWithoutRadio = Array.from(headers).find(
              (header) => !header.querySelector('.adyen-checkout__payment-method__radio')
            );
  
            if (firstWithoutRadio) {
              const span = document.createElement('span');
              span.setAttribute('aria-hidden', 'true');
              span.classList.add('adyen-checkout__payment-method__radio', 'adyen-checkout__payment-method__radio--selected');
              firstWithoutRadio.appendChild(span);
            }
  
            headers.forEach((header) => {
              header.addEventListener('click', () => {
                document.querySelectorAll('.adyen-checkout__payment-method__radio--selected')
                  .forEach((el) => el.classList.remove('adyen-checkout__payment-method__radio--selected'));

                const span = header.querySelector('.adyen-checkout__payment-method__radio');
                if (span) {
                  span.classList.add('adyen-checkout__payment-method__radio--selected');
                }
              });
            });


          },
        });
  
        if (containerRef.current) {
          dropinRef.current.mount(containerRef.current);
        }
      };
  
      initializeAdyen();
  
      return () => {
        dropinRef.current?.unmount();

        const headers = document.querySelectorAll('.adyen-checkout__payment-method__header');
        headers.forEach((header) => {
          header.removeEventListener('click', () => {});
        });

        if (disclaimerRef.current) {
          disclaimerRef.current.remove();
          disclaimerRef.current = null;
        }
      };
    }, [buildAdyenConfig]);

    // Remove disclaimer once identity verification is completed
    useEffect(() => {
      if (callbacks.isIdVerified && disclaimerRef.current) {
        disclaimerRef.current.remove();
        disclaimerRef.current = null;
      }
    }, [callbacks.isIdVerified]);
  
    // ============================================================================
    // Render
    // ============================================================================
  
    return <div ref={containerRef} id="dropin-container" />;
  };
  
  export default AdyenCheckoutForm;